<template>
  <div class="documents-tab">
    <!-- Title Row -->
    <div class="doc-section-title-row">
      <div class="doc-section-title">Latest Documents</div>
      <a class="doc-turn-link" @click="goToDocCenter">Turn to Document Management Center</a>
    </div>

    <!-- Document Table -->
    <el-table
      ref="docTable"
      :data="docTypes"
      size="small"
      style="width:100%;min-width:1160px"
      :header-cell-style="{background:'#F5F7FA',color:'#1F2937',fontWeight:'500',fontSize:'12px'}"
      :cell-style="{fontSize:'13px'}"
      row-key="key"
      :expand-row-keys="expandedKeys"
      @expand-change="onExpandChange"
    >
      <!-- Expand Column (hidden arrow, controlled by View History button) -->
      <el-table-column type="expand" width="1" class-name="doc-expand-col">
        <template slot-scope="scope">
          <div class="doc-history-expand">
            <div v-if="historyLoadingMap[scope.row.key]" class="doc-history-row"><span class="doc-history-empty">Loading...</span></div>
            <div v-else-if="getHistory(scope.row.key).length === 0" class="doc-history-row"><span class="doc-history-empty">No Data</span></div>
            <template v-else>
              <div v-for="(h, idx) in getPageHistory(scope.row.key)" :key="idx" class="doc-history-row">
                <span class="doc-hc doc-hc-type"></span>
                <span class="doc-hc doc-hc-period">{{ h.period }}</span>
                <span class="doc-hc doc-hc-time">{{ h.uploadTime ? moment(h.uploadTime).format('YYYY-MM-DD HH:mm') : '' }}</span>
                <span class="doc-hc doc-hc-by">{{ h.uploadBy }}</span>
                <span class="doc-hc doc-hc-name"><a class="doc-download-link" :title="h.attachment" @click="handleDocHistoryDownload(h)">{{ h.attachment }}</a></span>
                <el-tooltip :content="h.remark" placement="top" :open-delay="300">
                  <span class="doc-hc doc-hc-remark">{{ h.remark }}</span>
                </el-tooltip>
                <span class="doc-hc doc-hc-op"></span>
              </div>
              <div class="doc-history-pagination">
                <span class="doc-history-total">Total {{ getHistory(scope.row.key).length }}</span>
                <el-pagination
                  small
                  layout="prev, pager, next"
                  :total="getHistory(scope.row.key).length"
                  :current-page.sync="historyPageMap[scope.row.key].page"
                  :page-size="historyPageSize"
                  @current-change="onHistoryPageChange(scope.row.key, $event)"
                ></el-pagination>
              </div>
            </template>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Document Type" min-width="1" align="left">
        <template slot-scope="scope">
          <div class="doc-type-cell">
            <span class="doc-type-dot" :style="{background: scope.row.color}"></span>
            <el-tooltip :content="scope.row.name" placement="top" :open-delay="300">
              <span class="doc-type-name">{{ scope.row.name }}</span>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Period" width="90" align="left">
        <template slot-scope="scope">
          <span v-if="scope.row.latest" class="doc-period-text">{{ scope.row.latest.period }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Upload Time" width="120" align="left">
        <template slot-scope="scope">
          <span v-if="scope.row.latest" class="doc-upload-time">{{ scope.row.latest.uploadTime ? moment(scope.row.latest.uploadTime).format('YYYY-MM-DD HH:mm') : '' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Uploaded By" width="110" align="left">
        <template slot-scope="scope">
          <span v-if="scope.row.latest" class="doc-upload-by">{{ scope.row.latest.uploadBy }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Document Name" min-width="1" align="left">
        <template slot-scope="scope">
          <a v-if="scope.row.latest" class="doc-download-link" :title="scope.row.latest.attachment" @click="handleDocDownload(scope.row.key)">{{ scope.row.latest.attachment }}</a>
          <span v-else style="color:#8C8C8C;">No Data</span>
        </template>
      </el-table-column>
      <el-table-column label="Remark" width="150" align="left">
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.latest" :content="scope.row.latest.remark" placement="top" :open-delay="300">
            <span class="doc-remark-text">{{ scope.row.latest.remark }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="Operation" width="340" align="center">
        <template slot-scope="scope">
          <div class="doc-btn-cell">
            <button class="doc-table-btn doc-btn-dl" @click="handleDownloadTemplate(scope.row.name)">Download Template</button>
            <button class="doc-table-btn doc-btn-add" @click="openModifyModal(scope.row, 'add')">Add</button>
            <button v-if="scope.row.latest" class="doc-table-btn doc-btn-modify" @click="openModifyModal(scope.row, 'modify')">Modify</button>
            <button class="doc-table-btn doc-btn-history" :class="{'active': expandedKeys.includes(scope.row.key)}" @click="toggleHistory(scope.row)">{{ expandedKeys.includes(scope.row.key) ? 'Hide History' : 'View History' }}</button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- Modify / Add Document Modal -->
    <el-dialog :title="modalTitle" :visible.sync="modifyVisible" width="825px" custom-class="modify-doc-dialog" :close-on-click-modal="false">
      <!-- Account Info -->
      <div class="modify-doc-info">
        <div class="modify-doc-info-row">
          <div class="modify-doc-info-item">
            <span class="modify-doc-info-label">Country:</span>
            <span class="modify-doc-info-value">{{ accountInfo.country }}</span>
          </div>
        </div>
        <div class="modify-doc-info-row">
          <div class="modify-doc-info-item">
            <span class="modify-doc-info-label">Account Service No. (GTS):</span>
            <span class="modify-doc-info-value">{{ accountInfo.gtsCode }}</span>
          </div>
          <div class="modify-doc-info-item">
            <span class="modify-doc-info-label">Account Name:</span>
            <span class="modify-doc-info-value">{{ accountInfo.accountName }}</span>
          </div>
        </div>
      </div>

      <!-- Attachment Section -->
      <div class="modify-doc-attachment">
        <div class="modify-doc-att-header">
          <span class="modify-doc-att-name">{{ modifyDocType }}</span>
          <span class="modify-doc-att-colon">:</span>
        </div>
        <el-form ref="modifyForm" :model="modifyForm" :rules="modifyRules" label-width="88px" label-position="right" size="mini">
          <el-form-item label="Document" prop="fileId">
            <el-upload
              class="doc-upload-btn"
              action="#"
              :http-request="uploadFile"
              :show-file-list="false"
              :before-upload="beforeUpload"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png,.gif,.mp4,.msg,.bmp,.jpeg"
            >
              <el-button size="mini" type="primary">Upload</el-button>
            </el-upload>
            <div class="modify-doc-att-file-info" v-if="modifyFileName">
              <a class="doc-upload-file-name" :title="modifyFileName">
                <i class="el-icon-document"></i>{{ modifyFileName }}
              </a>
              <label>
                <span class="doc-upload-percentage" v-if="uploadPercentage > 0 && uploadPercentage < 100">{{ uploadPercentage }}%</span>
                <i class="el-icon-circle-check" style="color: #67c23a" v-if="uploadPercentage === 100"></i>
                <i class="el-icon-close doc-upload-icon-close" @click="handleRemoveFile"></i>
              </label>
              <el-progress v-if="uploadPercentage > 0 && uploadPercentage < 100" :show-text="false" :stroke-width="2" :percentage="uploadPercentage"></el-progress>
            </div>
          </el-form-item>
          <el-form-item label="Period" prop="year" v-if="showPeriod">
            <el-date-picker v-model="modifyForm.year" type="year" value-format="yyyy" placeholder="Year" style="width:160px;margin-right:8px;" :disabled="modifyIsAdd === false"></el-date-picker>
            <el-select v-if="showQuarter" v-model="modifyForm.quarter" placeholder="Quarter" clearable style="width:150px;" :disabled="modifyIsAdd === false">
              <el-option v-for="q in quarterOptions" :key="q" :label="q" :value="q"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Quarter" prop="quarter" v-if="showQuarter" class="doc-quarter-hidden-label"></el-form-item>
          <el-form-item label="Remark">
            <el-input type="textarea" :rows="10" v-model="modifyForm.remark" placeholder="Please input remark" maxlength="500" show-word-limit></el-input>
          </el-form-item>
        </el-form>
        <div class="modify-doc-tips">
          <span class="modify-doc-tips-title">Tips:</span> Maximum Document Size: 50M; Format: docx, xlsx, pptx, png, jpeg, bmp, mp4, msg.
        </div>
      </div>

      <span slot="footer">
        <el-button size="small" @click="modifyVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="modifyLoading" @click="saveModify">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
export default {
  name: 'DocumentsTab',
  props: {
    accountInfo: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      moment,
      expandedKeys: [],
      historyLoadingMap: {},
      historyPageMap: {},
      historyPageSize: 5,
      modifyVisible: false,
      modifyLoading: false,
      modalTitle: 'Modify Document',
      modifyDocType: '',
      modifyDocKey: '',
      modifyIsAdd: false,
      modifyDocId: null,
      modifyFileName: '',
      modifyFile: null,
      uploadPercentage: 0,
      cancelUpload: null,
      quarterOptions: ['Q1', 'Q2', 'Q3', 'Q4'],
      modifyForm: {
        fileId: '',
        year: null,
        quarter: '',
        remark: '',
      },
      modifyRules: {
        fileId: [
          { required: true, validator: this.validateFile, trigger: 'change' }
        ],
        year: [
          { required: true, message: 'Please select Year', trigger: 'change' }
        ],
        quarter: [
          { required: true, message: 'Please select Quarter', trigger: 'change' }
        ],
      },
      docTypes: [],
      docHistoryMap: {},
      // color map by docType full name
      docTypeColorMap: {
        'Account Profile': '#1890ff',
        'QBR': '#faad14',
        'Visit Minutes': '#52c41a',
        'Inspection Report': '#722ed1',
        'Major Incident Support Summary': '#eb2f96',
        'Review Summary': '#13c2c2',
        'Other Documents': '#fa541c',
        'Web to case (key Incidents)': '#2f54eb',
        'Order (key Incidents)': '#595959',
      },
    }
  },
  computed: {
    accountId() {
      return this.$route.params.id
    },
    showPeriod() {
      return ['Account Profile', 'QBR', 'Visit Minutes'].includes(this.modifyDocKey)
    },
    showQuarter() {
      return ['QBR', 'Visit Minutes'].includes(this.modifyDocKey)
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      if (!this.accountId) return
      let url = `${this.$store.state.islhurl}valued-account/${this.accountId}/documents`
      this._get(url).then(res => {
        if (res.code === 200 && res.data) {
          const docs = res.data
          this.docTypes = docs.map(d => ({
            key: d.docType,
            name: d.docType,
            color: d.color || this.docTypeColorMap[d.docType] || '#999',
            hasCurrent: d.hasCurrent,
            latest: d.hasCurrent ? {
              id: d.id,
              period: d.periodDisplay,
              uploadTime: d.uploadTime || '',
              uploadBy: d.uploadedBy || '',
              attachment: d.fileName || '',
              fileId: d.fileId,
              remark: d.remark || '',
              periodYear: d.periodYear,
              periodQuarter: d.periodQuarter,
            } : null,
          }))
        }
      }).catch(() => {
        this.docTypes = []
      })
    },
    goToDocCenter() {
      this.$router.push({ name: 'documentManagement' })
    },
    getHistory(key) {
      return this.docHistoryMap[key] || []
    },
    getPageHistory(key) {
      const all = this.docHistoryMap[key] || []
      const pg = this.historyPageMap[key] || { page: 1 }
      const start = (pg.page - 1) * this.historyPageSize
      return all.slice(start, start + this.historyPageSize)
    },
    onHistoryPageChange(key, page) {
      this.$set(this.historyPageMap, key, { ...(this.historyPageMap[key] || {}), page })
    },
    onExpandChange(_row, expandedRows) {
      this.expandedKeys = expandedRows.map(r => r.key)
    },
    toggleHistory(row) {
      const table = this.$refs.docTable
      const isExpanded = this.expandedKeys.includes(row.key)
      if (!isExpanded) {
        this.$set(this.historyPageMap, row.key, { page: 1 })
        this.loadHistory(row.key)
      }
      table.toggleRowExpansion(row, !isExpanded)
    },
    loadHistory(key) {
      if (!this.accountId) return
      this.$set(this.historyLoadingMap, key, true)
      let url = `${this.$store.state.islhurl}valued-account/${this.accountId}/documents/history?docType=${key}`
      this._get(url).then(res => {
        this.$set(this.historyLoadingMap, key, false)
        if (res.code === 200 && res.data) {
          const list = res.data.map(d => ({
            id: d.id,
            period: d.periodDisplay,
            uploadTime: d.uploadTime || '',
            uploadBy: d.uploadedBy || '',
            attachment: d.fileName || '',
            fileId: d.fileId,
            remark: d.remark || '',
          }))
          this.$set(this.docHistoryMap, key, list)
        } else {
          this.$set(this.docHistoryMap, key, [])
        }
      }).catch(() => {
        this.$set(this.historyLoadingMap, key, false)
        this.$set(this.docHistoryMap, key, [])
      })
    },
    refreshHistoryIfExpanded() {
      const key = this.modifyDocKey
      if (!key || !this.expandedKeys.includes(key)) return
      this.$nextTick(() => {
        const table = this.$refs.docTable
        const row = this.docTypes.find(t => t.key === key)
        if (table && row) {
          // collapse then re-expand to force reload with fresh data
          table.toggleRowExpansion(row, false)
          this.$nextTick(() => {
            table.toggleRowExpansion(row, true)
          })
        }
        this.$set(this.historyPageMap, key, { page: 1 })
        this.loadHistory(key)
      })
    },
    _getFileNameFromResponse(response) {
      const disposition = response.headers && response.headers['content-disposition']
      console.log('disposition', disposition)
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)
        if (match && match[1]) {
          return decodeURIComponent(match[1].replace(/"/g, ''))
        }
      }
      return null
    },
    _downloadFile(fileId, fileName) {
      if (!fileId) {
        this.$message.warning('No file to download')
        return
      }
      let url = `${this.$store.state.islhurl}attachment/download/${fileId}`
      this.$axios({
        url,
        method: 'get',
        responseType: 'blob'
      }).then(response => {
        const blob = new Blob([response.data])
        const aEle = document.createElement('a')
        const href = window.URL.createObjectURL(blob)
        aEle.href = href
        aEle.download = this._getFileNameFromResponse(response) || fileName || 'document'
        document.body.appendChild(aEle)
        aEle.click()
        document.body.removeChild(aEle)
        window.URL.revokeObjectURL(href)
      }).catch(() => {
        this.$message.error('Download failed!')
      })
    },
    handleDocDownload(key) {
      const row = this.docTypes.find(t => t.key === key)
      if (row && row.latest && row.latest.fileId) {
        this._downloadFile(row.latest.fileId, row.latest.attachment)
      } else {
        this.$message.warning('No file to download')
      }
    },
    handleDocHistoryDownload(item) {
      if (item && item.fileId) {
        this._downloadFile(item.fileId, item.attachment)
      } else {
        this.$message.warning('No file to download')
      }
    },
    handleDownloadTemplate(docType) {
      if (!docType) return
      let url = `${this.$store.state.islhurl}valued-account/document/template?docType=${encodeURIComponent(docType)}`
      this.$axios({
        url,
        method: 'get',
        responseType: 'blob'
      }).then(response => {
        const blob = new Blob([response.data])
        const aEle = document.createElement('a')
        const href = window.URL.createObjectURL(blob)
        aEle.href = href
        aEle.download = this._getFileNameFromResponse(response) || (docType + '_Template')
        document.body.appendChild(aEle)
        aEle.click()
        document.body.removeChild(aEle)
        window.URL.revokeObjectURL(href)
      }).catch(() => {
        this.$message.error('Download template failed!')
      })
    },
    openModifyModal(docType, mode) {
      this.modifyDocType = docType.name
      this.modifyDocKey = docType.key
      this.modifyIsAdd = mode === 'add' || !docType.latest
      this.modifyDocId = docType.latest ? docType.latest.id : null
      this.modalTitle = this.modifyIsAdd ? 'Add Document' : 'Modify Document'
      this.modifyFileName = ''
      this.modifyFile = null
      this.uploadPercentage = 0
      this.cancelUpload = null
      this.modifyForm = {
        fileId: '',
        year: null,
        quarter: '',
        remark: '',
      }

      // For modify mode, show current filename & pre-fill period/remark
      if (!this.modifyIsAdd && docType.latest) {
        this.modifyFileName = docType.latest.attachment || ''
        this.modifyForm.fileId = 'existing'
        this.modifyForm.remark = docType.latest.remark || ''
        if (docType.latest.periodYear) {
          this.modifyForm.year = docType.latest.periodYear
        }
        if (docType.latest.periodQuarter) {
          this.modifyForm.quarter = docType.latest.periodQuarter
        }
      }

      this.modifyVisible = true
      this.$nextTick(() => {
        if (this.$refs.modifyForm) this.$refs.modifyForm.clearValidate()
      })
    },
    validateFile(rule, value, callback) {
      if (this.uploadPercentage > 0 && this.uploadPercentage < 100) {
        callback(new Error('Please wait for the upload to complete before saving.'))
      } else if (!value) {
        callback(new Error('Please upload an attachment.'))
      } else {
        callback()
      }
    },
    beforeUpload(file) {
      // if (this.modifyFileName) {
      //   this.$message.warning('Only one file is allowed for upload. The previous file will be replaced.')
      // }
      const isLt50M = file.size / 1024 / 1024 < 50
      if (!isLt50M) {
        this.$message.error('File cannot be larger than 50MB!')
        return false
      }
      return true
    },

    uploadFile(row) {
      if (typeof this.cancelUpload === 'function') {
        this.cancelUpload('Replaced by new upload')
        this.cancelUpload = null
      }
      const file = row.file
      let fd = new FormData()
      fd.append('file', file)
      fd.append('moduleType', 'VALUED_ACCOUNT')
      let url = `${this.$store.state.islhurl}attachment/upload`
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      this.cancelUpload = source.cancel
      this.modifyFile = file
      this.modifyFileName = file.name
      this.modifyForm.fileId = ''
      this.uploadPercentage = 0
      this._post(url, fd, {
        cancelToken: source.token,
        onUploadProgress: (percent) => {
          this.uploadPercentage = percent === 100 ? 99 : percent
        },
        noProgress: true
      }).then(res => {
        if (res.code == 200) {
          this.uploadPercentage = 100
          this.modifyForm.fileId = res.data.id || res.data
          this.$refs.modifyForm.clearValidate('fileId')
        } else {
          this.modifyFileName = ''
          this.modifyFile = null
          this.modifyForm.fileId = ''
          this.uploadPercentage = 0
          this.$message.error(res.message || 'Upload failed!')
        }
      }).catch(error => {
        console.log(error)
        this.modifyFileName = ''
        this.modifyFile = null
        this.modifyForm.fileId = ''
        this.uploadPercentage = 0
      })
    },
    handleRemoveFile() {
      if (typeof this.cancelUpload === 'function') {
        this.cancelUpload('User canceled upload')
        this.cancelUpload = null
      }
      this.modifyFileName = ''
      this.modifyFile = null
      this.modifyForm.fileId = ''
      this.uploadPercentage = 0
    },
    saveModify() {
      this.$refs.modifyForm.validate((valid) => {
        if (!valid) return
        this.modifyLoading = true

        if (this.modifyIsAdd) {
          // Add: POST /valued-account/document
          const param = {
            customerId: this.accountId,
            docType: this.modifyDocKey,
            fileId: this.modifyForm.fileId,
          }
          if (this.showPeriod && this.modifyForm.year) {
            param.periodYear = this.modifyForm.year
          }
          if (this.showQuarter && this.modifyForm.quarter) {
            param.periodQuarter = this.modifyForm.quarter
          }
          if (this.modifyForm.remark) {
            param.remark = this.modifyForm.remark
          }
          let url = `${this.$store.state.islhurl}valued-account/document`
          this._post(url, param).then(res => {
            this.modifyLoading = false
            if (res.code === 200) {
              this.$message.success('Document saved successfully!')
              this.modifyVisible = false
              this.loadData()
              this.refreshHistoryIfExpanded()
            } else {
              this.$message.error(res.msg || 'Save failed!')
            }
          }).catch(() => {
            this.modifyLoading = false
            this.$message.error('Save failed!')
          })
        } else {
          // Modify: PUT /valued-account/document/{docId}
          const param = {}
          // fileId: if user uploaded new file, pass new fileId; otherwise don't pass (only update period/remark)
          if (this.modifyForm.fileId && this.modifyForm.fileId !== 'existing') {
            param.fileId = this.modifyForm.fileId
          }
          if (this.showPeriod && this.modifyForm.year) {
            param.periodYear = this.modifyForm.year
          }
          if (this.showQuarter && this.modifyForm.quarter) {
            param.periodQuarter = this.modifyForm.quarter
          }
          if (this.modifyForm.remark !== undefined) {
            param.remark = this.modifyForm.remark
          }
          let url = `${this.$store.state.islhurl}valued-account/document/${this.modifyDocId}`
          this._put(url, param).then(res => {
            this.modifyLoading = false
            if (res.code === 200) {
              this.$message.success('Document saved successfully!')
              this.modifyVisible = false
              this.loadData()
              this.refreshHistoryIfExpanded()
            } else {
              this.$message.error(res.message || 'Save failed!')
            }
          }).catch(() => {
            this.modifyLoading = false
            this.$message.error('Save failed!')
          })
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
.documents-tab {
  padding: 20px 24px;
  overflow-x: auto;

  .doc-section-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }
  .doc-section-title {
    font-size: 13px;
    font-weight: 500;
    color: #1F2937;
  }
  .doc-turn-link {
    color: #007AFF;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
    &:hover { color: #3494ff; }
  }

  // Document type cell
  .doc-type-cell {
    display: flex;
    align-items: center;
  }
  .doc-type-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 6px;
  }
  .doc-type-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1F2937;
  }
  .doc-period-text { color: #8C8C8C; }
  .doc-upload-time { color: #8C8C8C; font-size: 12px; }
  .doc-upload-by { color: #1F2937; }
  .doc-remark-text {
    color: #8C8C8C;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
  }

  // Download link
  .doc-download-link {
    color: #007AFF;
    cursor: pointer;
    text-decoration: underline;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
    &:hover { color: #3494ff; }
  }

  // Operation buttons
  .doc-btn-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .doc-table-btn {
    height: 24px;
    padding: 0 8px;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .doc-btn-dl {
    border: 1px solid rgb(64,158,255);
    background: #fff;
    color: rgb(64,158,255);
    &:hover { background: rgb(64,158,255); color: #fff; }
  }
  .doc-btn-add {
    border: none;
    background: rgb(64,158,255);
    color: #fff;
    &:hover { background: rgb(48,140,230); }
  }
  .doc-btn-modify {
    border: none;
    background: rgb(64,158,255);
    color: #fff;
    &:hover { background: rgb(48,140,230); }
  }
  .doc-btn-history {
    border: 1px solid #8C8C8C;
    background: #fff;
    color: #8C8C8C;
    &:hover { background: #8C8C8C; color: #fff; }
    &.active {
      border: 1px solid #007AFF;
      background: #007AFF;
      color: #fff;
      &:hover { background: rgb(48,140,230); }
    }
  }

  // History expand row (inline under the current row)
  .doc-history-expand {
    background: #FAFBFC;
  }
  .doc-history-row {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #F0F0F0;
    &:last-child { border-bottom: none; }
  }
  .doc-history-empty {
    padding: 12px;
    color: #8C8C8C;
    font-size: 12px;
    text-align: center;
    flex: 1;
  }
  .doc-hc {
    padding: 8px 10px;
    font-size: 12px;
    color: #8C8C8C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
  }
  // Fixed columns match parent table width exactly
  // Elastic columns (Document Name & Remark) share remaining space equally via flex:1
  .doc-hc-type   { flex: 1; min-width: 0; }
  .doc-hc-period { width: 90px; flex-shrink: 0; }
  .doc-hc-time   { width: 120px; flex-shrink: 0; }
  .doc-hc-by     { width: 110px; flex-shrink: 0; }
  .doc-hc-name   { flex: 1; min-width: 0; }
  .doc-hc-remark { width: 150px; flex-shrink: 0; }
  .doc-hc-op     { width: 340px; flex-shrink: 0; }

  // History pagination
  .doc-history-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 10px;
    gap: 12px;
  }
  .doc-history-total {
    font-size: 12px;
    color: #8C8C8C;
  }

  // Modify dialog styles
  .modify-doc-info {
    margin-bottom: 20px;
  }
  .modify-doc-info-row {
    display: flex;
    gap: 24px;
    align-items: baseline;
    &:not(:first-child) { margin-top: 8px; }
  }
  .modify-doc-info-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }
  .modify-doc-info-label {
    font-size: 13px;
    color: #8C8C8C;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .modify-doc-info-value {
    font-size: 13px;
    color: #1F2937;
    min-width: 85px;
  }

  .modify-doc-attachment {
    border: 1px solid #E5E7EB;
    border-radius: 4px;
    padding: 16px;
    background: #FAFAFA;
  }
  .modify-doc-att-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: nowrap;
    white-space: nowrap;
    overflow: hidden;
  }
  .modify-doc-att-name {
    font-size: 13px;
    color: #1F2937;
    font-weight: 500;
  }
  .modify-doc-att-colon {
    font-size: 13px;
    color: #1F2937;
  }
  .doc-upload-btn {
    display: inline-block;
  }
  .modify-doc-att-file-info {
    margin-top: 8px;
    border-radius: 4px;
    position: relative;
    line-height: 26px;
    &:hover {
      background-color: #f5f7fa;
      .el-icon-circle-check, .doc-upload-percentage {
        display: none;
      }
      .doc-upload-icon-close {
        display: inline-block !important;
      }
    }
  }
  .doc-upload-file-name {
    line-height: 26px;
    color: #606266;
    display: block;
    margin-right: 40px;
    overflow: hidden;
    padding-left: 4px;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    &:hover {
      color: #409eff;
    }
    i {
      margin-right: 7px;
      color: #909399;
    }
  }
  .modify-doc-att-file-info label {
    position: absolute;
    right: 5px;
    top: 0;
    line-height: 26px;
    i {
      line-height: 26px;
    }
  }
  .doc-upload-percentage {
    color: #666;
    font-size: 12px;
  }
  .doc-upload-icon-close {
    cursor: pointer;
    display: none !important;
    color: #909399;
  }

  // Form styles inside the attachment area
  .modify-doc-attachment .el-form-item {
    margin-bottom: 18px;
  }
  .modify-doc-attachment .el-form-item__label {
    font-size: 12px;
    color: #8C8C8C;
  }
  .doc-quarter-hidden-label {
    margin-top: -18px;
    margin-bottom: 0 !important;
    /deep/ .el-form-item__label {
      visibility: hidden;
    }
    /deep/ .el-form-item__content {
      display: none;
    }
}
  /deep/.el-form-item__content .el-input__suffix {
    left: auto;
    right: 10px;
  }
  .modify-doc-tips {
    margin-top: 12px;
    font-size: 12px;
    // color: #8C8C8C;
    line-height: 1.8;
    color: #409eff;
  }
  .modify-doc-tips-title {
    font-size: 12px;
    color: #1F2937;
    font-weight: 500;
  }
}
</style>

<style>
/* Dialog-level styles (not scoped) */
.modify-doc-dialog .el-dialog__body {
  padding: 20px 24px;
}
.modify-doc-dialog .el-form-item--mini .el-form-item__error {
  padding-top: 3px;
}
/* Hide the default expand arrow for the doc expand column */
.doc-expand-col .el-table__expand-icon {
  display: none;
}
.doc-expand-col {
  padding: 0 !important;
}
/* Remove padding from expand cell so flex rows align with parent table columns */
.el-table__expanded-cell.doc-expand-col {
  padding: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/view/valuedAccountManagement/accountDetailTabs/Documents.vue